import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import useIdleTimeout from '../lib/useIdleTimeout';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Auto-logout after this many milliseconds of inactivity.
// (60 seconds per user request; change here if a longer window is needed.)
export const SESSION_IDLE_TIMEOUT_MS = 60 * 1000;

// Configure axios immediately at module load time
const initToken = localStorage.getItem('token');
const initBusinessId = localStorage.getItem('selectedBusinessId');
if (initToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${initToken}`;
}
if (initBusinessId) {
  axios.defaults.headers.common['X-Business-Id'] = initBusinessId;
}

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [token, setToken] = useState(initToken);
  const [loading, setLoading] = useState(true);

  // Configure axios defaults when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Single-business system: keep the business id header in sync (backend ignores it)
  useEffect(() => {
    document.documentElement.classList.remove('theme-light');
    if (selectedBusiness) {
      axios.defaults.headers.common['X-Business-Id'] = selectedBusiness.id;
      localStorage.setItem('selectedBusinessId', selectedBusiness.id);
    } else {
      delete axios.defaults.headers.common['X-Business-Id'];
    }
  }, [selectedBusiness]);

  // Load user on mount
  const loadUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    // Try to load user from localStorage first (for fast page loads after login)
    const savedUser = localStorage.getItem('user');
    const savedBusinesses = localStorage.getItem('businesses');
    
    if (savedUser && savedBusinesses) {
      try {
        const userData = JSON.parse(savedUser);
        const businessList = JSON.parse(savedBusinesses);
        setUser(userData);
        setBusinesses(businessList);
        if (businessList.length > 0) {
          setSelectedBusiness(businessList[0]);
          axios.defaults.headers.common['X-Business-Id'] = businessList[0].id;
        }
        // Refresh the (single) business in background so cached names never go stale
        axios.get(`${API_URL}/businesses`).then(({ data }) => {
          if (data?.length) {
            setBusinesses(data);
            setSelectedBusiness(data[0]);
            localStorage.setItem('businesses', JSON.stringify(data));
          }
        }).catch(() => {});
        
        setLoading(false);
        return; // Skip API call if we have cached data
      } catch (e) {
        console.error('Failed to parse cached user data:', e);
      }
    }

    // Ensure headers are set before making request
    if (!axios.defaults.headers.common['Authorization']) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));

      // Load businesses
      const bizResponse = await axios.get(`${API_URL}/businesses`);
      setBusinesses(bizResponse.data);
      localStorage.setItem('businesses', JSON.stringify(bizResponse.data));

      if (bizResponse.data.length > 0) {
        setSelectedBusiness(bizResponse.data[0]);
      }
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData, businesses: userBusinesses } = response.data;
    
    // Set headers FIRST before updating state
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    localStorage.setItem('token', access_token);
    
    // Save user and businesses to localStorage for caching
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('businesses', JSON.stringify(userBusinesses));
    
    const selectedBiz = userBusinesses[0] || null;
    
    if (selectedBiz) {
      axios.defaults.headers.common['X-Business-Id'] = selectedBiz.id;
      localStorage.setItem('selectedBusinessId', selectedBiz.id);
      setSelectedBusiness(selectedBiz);
    }
    
    setToken(access_token);
    setUser(userData);
    setBusinesses(userBusinesses);
    
    return response.data;
  };

  const loginWithPin = async (userId, pinCode) => {
    const response = await axios.post(`${API_URL}/auth/login-pin`, { 
      user_id: userId, 
      pin_code: pinCode 
    });
    const { access_token, user: userData, businesses: userBusinesses } = response.data;
    
    // Set headers FIRST before updating state
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    localStorage.setItem('token', access_token);
    
    // Save user and businesses to localStorage for caching
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('businesses', JSON.stringify(userBusinesses));
    
    const selectedBiz = userBusinesses[0] || null;
    
    if (selectedBiz) {
      axios.defaults.headers.common['X-Business-Id'] = selectedBiz.id;
      localStorage.setItem('selectedBusinessId', selectedBiz.id);
      setSelectedBusiness(selectedBiz);
    }
    
    setToken(access_token);
    setUser(userData);
    setBusinesses(userBusinesses);
    
    return response.data;
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('selectedBusinessId');
    localStorage.removeItem('user');
    localStorage.removeItem('businesses');
    localStorage.removeItem('lastActivityAt');
    setToken(null);
    setUser(null);
    setBusinesses([]);
    setSelectedBusiness(null);
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Business-Id'];
  }, []);

  // Auto-logout when user has been idle.
  const handleIdleTimeout = useCallback(() => {
    if (!token) return;
    logout();
    toast.warning('Sesión cerrada por inactividad. Vuelve a iniciar sesión.');
  }, [token, logout]);

  useIdleTimeout({
    enabled: !!token,
    timeoutMs: SESSION_IDLE_TIMEOUT_MS,
    onTimeout: handleIdleTimeout,
  });

  const isAdmin = () => {
    return user?.role === 'admin' || user?.role === 'master_admin' || user?.is_master_admin;
  };

  const isBarber = () => {
    return user?.role === 'barber' || user?.role === 'stylist';
  };

  const isClient = () => {
    return user?.role === 'client';
  };

  const value = {
    user,
    token,
    businesses,
    selectedBusiness,
    loading,
    login,
    loginWithPin,
    logout,
    isAdmin,
    isBarber,
    isClient,
    refreshUser: loadUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
